import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, Edit3 } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';
import UniversalList from '../components/UniversalList';

const PAGE_SIZE_DEFAULT = 20;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const canAdd = hasPermission('settings_attributes', 'add');
  const canEdit = hasPermission('settings_attributes', 'edit');
  const canDelete = hasPermission('settings_attributes', 'delete');
  const canView = hasPermission('settings_attributes', 'view');
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const cleanLabel = (label) => {
    return label ? label.replace(/\d+$/, '') : label;
  };

  const loadAttributes = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/attributes');
      const data = await res.json();
      setAttributes(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttributes();
  }, []);

  // Reset page on search
  useEffect(() => { setPage(1); }, [searchTerm]);

  // Client-side filter
  const filteredAttributes = useMemo(() => {
    if (!searchTerm) return attributes;
    const q = searchTerm.toLowerCase();
    return attributes.filter(a =>
      (a.attribute_code || '').toLowerCase().includes(q) ||
      (cleanLabel(a.default_label) || '').toLowerCase().includes(q) ||
      (a.input_type || '').toLowerCase().includes(q)
    );
  }, [attributes, searchTerm]);

  // Client-side paginate
  const totalItems = filteredAttributes.length;
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAttributes.slice(start, start + pageSize);
  }, [filteredAttributes, page, pageSize]);

  const handleDelete = async (attributeId, attributeCode, isSystem) => {
    if (isSystem) {
      notify(t('attribute_sets.system_cannot_delete'), 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete attribute "${attributeCode}"? This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await fetch(`/api/settings/attributes/${attributeId}/delete`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.status === 'success') {
        notify(`Attribute "${attributeCode}" deleted successfully`, 'success');
        loadAttributes();
      } else {
        notify(result.message || 'Failed to delete attribute', 'error');
      }
    } catch (err) {
      notify(err.message || 'An error occurred while deleting the attribute', 'error');
    }
  };

  const columns = [
    {
      key: 'attribute_code',
      label: t('settings.attr_code'),
      className: 'font-mono text-sm dark:text-slate-300',
      render: (val, row) => (
        <span className="inline-flex items-center gap-2">
          <span>{val}</span>
          {row.is_system === 1 && (
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              系统
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'default_label',
      label: t('settings.attr_label'),
      render: (val) => cleanLabel(val),
    },
    {
      key: 'input_type',
      label: t('settings.attr_type'),
      className: 'text-slate-500 dark:text-slate-400',
    },
    {
      key: 'source',
      label: t('settings.source') || 'Source',
      render: (val) => {
        if (val === 'magento2') {
          return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
            M2
          </span>;
        }
        if (val === 'system') {
          return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            {t('settings.system') || 'System'}
          </span>;
        }
        return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>;
      },
    },
    {
      key: 'actions',
      label: t('common.actions'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (val, row) => (
        <div className="flex gap-1 items-center justify-end">
          {canView && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/attributes/view/${row.attribute_id}`); }}
              className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              title={t('common.view')}
            >
              <Eye size={16} />
            </button>
          )}
          {canEdit && row.source !== 'magento2' && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/attributes/edit/${row.attribute_id}`); }}
              className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title={t('common.edit')}
            >
              <Edit3 size={16} />
            </button>
          )}
          {canEdit && row.source === 'magento2' && (
            <span
              className="p-1.5 text-slate-300 dark:text-slate-600 cursor-not-allowed inline-block"
              title={t('settings.m2_readonly') || 'M2同步属性，只读'}
            >
              <Edit3 size={16} />
            </span>
          )}
          {canDelete && (
            row.is_system ? (
              <span
                className="p-1.5 text-slate-300 dark:text-slate-600 cursor-not-allowed inline-block"
                title={t('attribute_sets.system_cannot_delete')}
              >
                <Trash2 size={16} />
              </span>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(row.attribute_id, row.attribute_code || row.default_label, row.is_system); }}
                className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title={t('common.delete')}
              >
                <Trash2 size={16} />
              </button>
            )
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold">
          {t('settings.title') + ' - ' + t('settings.attribute_design')}
        </h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="md:hidden" />
        <div className="flex flex-wrap gap-3 md:justify-end">
          {canAdd && (
            <button
              onClick={() => navigate('/attributes/new')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              {t('common.add')}
            </button>
          )}
        </div>
      </div>

      <UniversalList
        searchPlaceholder={t('settings.search_attributes_placeholder')}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        columns={columns}
        data={pageData}
        loading={loading}
        emptyMessage={t('common.no_data')}
        rowKey="attribute_id"
        page={page}
        totalItems={totalItems}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        onRowClick={(row) => navigate(`/attributes/view/${row.attribute_id}`)}
      />
    </div>
  );
};

export default SettingsPage;
